/**
 * Client-side Event Bus
 *
 * Centralized event processing system with middleware support.
 * All events flow through this bus, enabling:
 * - Unified logging
 * - State updates
 * - Activity tracking
 * - Analytics
 * - Event replay
 */

import type {
  AgentEvent,
  EventType,
  EventMiddleware,
  EventHandler,
  EventSubscription,
} from './types.js';

export class ClientEventBus {
  private middlewares: EventMiddleware[] = [];
  private middlewareSet: Set<EventMiddleware> = new Set(); // Track unique middleware
  private listeners: Map<EventType | '*', Set<EventHandler>> = new Map();
  private eventHistory: AgentEvent[] = [];
  private maxHistorySize = 1000; // Keep last 1000 events

  /**
   * Register middleware to process all events
   * Middleware runs in registration order
   * Prevents duplicate registration
   */
  use(middleware: EventMiddleware): void {
    if (!this.middlewareSet.has(middleware)) {
      this.middlewares.push(middleware);
      this.middlewareSet.add(middleware);
    }
  }

  /**
   * Dispatch an event through the middleware pipeline
   * and notify all listeners
   */
  async dispatch(event: AgentEvent): Promise<void> {
    try {
      // Add to history
      this.eventHistory.push(event);
      if (this.eventHistory.length > this.maxHistorySize) {
        this.eventHistory.shift();
      }

      // Run through middleware pipeline
      let processedEvent = event;
      for (const middleware of this.middlewares) {
        processedEvent = await middleware(processedEvent);
      }

      // Notify type-specific listeners
      const typeListeners = this.listeners.get(processedEvent.type);
      if (typeListeners) {
        for (const handler of typeListeners) {
          try {
            await handler(processedEvent);
          } catch (error) {
            console.error(`[EventBus] Handler error for ${processedEvent.type}:`, error);
          }
        }
      }

      // Notify wildcard listeners
      const wildcardListeners = this.listeners.get('*');
      if (wildcardListeners) {
        for (const handler of wildcardListeners) {
          try {
            await handler(processedEvent);
          } catch (error) {
            console.error('[EventBus] Wildcard handler error:', error);
          }
        }
      }
    } catch (error) {
      console.error('[EventBus] Dispatch error:', error);
    }
  }

  /**
   * Subscribe to a specific event type
   */
  on<T extends AgentEvent>(
    eventType: EventType | '*',
    handler: EventHandler<T>
  ): EventSubscription {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    const handlers = this.listeners.get(eventType)!;
    handlers.add(handler as EventHandler);

    return {
      unsubscribe: () => {
        handlers.delete(handler as EventHandler);
        if (handlers.size === 0) {
          this.listeners.delete(eventType);
        }
      },
    };
  }

  /**
   * Subscribe to an event type for a single invocation
   */
  once<T extends AgentEvent>(
    eventType: EventType,
    handler: EventHandler<T>
  ): EventSubscription {
    const wrappedHandler: EventHandler<T> = async (event) => {
      subscription.unsubscribe();
      await handler(event);
    };

    const subscription = this.on(eventType, wrappedHandler);
    return subscription;
  }

  /**
   * Get event history
   */
  getHistory(filter?: {
    type?: EventType;
    sessionId?: string;
    since?: number;
  }): AgentEvent[] {
    let events = [...this.eventHistory];

    if (filter?.type) {
      events = events.filter((e) => e.type === filter.type);
    }

    if (filter?.sessionId) {
      events = events.filter((e) => 'sessionId' in e && e.sessionId === filter.sessionId);
    }

    if (filter?.since !== undefined) {
      events = events.filter((e) => e.timestamp >= filter.since!);
    }

    return events;
  }

  /**
   * Clear event history
   */
  clearHistory(): void {
    this.eventHistory = [];
  }

  /**
   * Replay events (useful after reconnection)
   */
  async replay(events: AgentEvent[]): Promise<void> {
    for (const event of events) {
      await this.dispatch(event);
    }
  }

  /**
   * Get listener count for debugging
   */
  getListenerCount(eventType?: EventType): number {
    if (eventType) {
      return this.listeners.get(eventType)?.size ?? 0;
    }

    let total = 0;
    for (const handlers of this.listeners.values()) {
      total += handlers.size;
    }
    return total;
  }

  /**
   * Clear all listeners and middleware (for cleanup)
   */
  reset(): void {
    this.listeners.clear();
    this.middlewares = [];
    this.middlewareSet.clear();
    this.eventHistory = [];
  }
}

/**
 * Global event bus instance
 */
export const eventBus = new ClientEventBus();
